// src/storage/usePendingPurchases.js
//
// Auth-bound, READ-ONLY listener on the caller's manual PayMe/FPS pending
// purchases (/pendingPurchases where uid == current uid). WP-3 MyPurchasesPanel
// source of truth (replaces the old localStorage pendingPurchases map).
//
// Results are sorted newest-first client-side (no composite index needed for a
// single-field equality query). Mock/unauth/no-db -> empty, not loading.
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';

function submittedMillis(p) {
    const v = p && p.submittedAt;
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
}

export function usePendingPurchases() {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (AUTH_MODE === 'mock' || !db || !uid) {
            setPending([]);
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        const q = query(collection(db, 'pendingPurchases'), where('uid', '==', uid));
        const unsub = onSnapshot(
            q,
            (snap) => {
                const rows = [];
                snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
                rows.sort((a, b) => submittedMillis(b) - submittedMillis(a));
                setPending(rows);
                setLoading(false);
            },
            (err) => {
                console.error('usePendingPurchases listener error:', err);
                setPending([]);
                setLoading(false);
            }
        );
        return () => unsub();
    }, [uid]);

    return { pending, loading };
}

export default usePendingPurchases;
